import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Pagination from "./Pagination";
import DealsList from "./DealsList";
import Loader from "./Loader";
import "../App.css";

const STORES = ["All", "Amazon", "Flipkart", "Myntra", "Meesho"];

const Home = () => {
  const [deals, setDeals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Client-side quick filter
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStore, setSelectedStore] = useState("All");

  useEffect(() => {
    fetchDeals(page);
  }, [page]);

  const fetchDeals = async (targetPage) => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || "https://api.updeals.in";
      const response = await axios.get(
        `${apiUrl}/deals?page=${targetPage}&limit=40`
      );
      setDeals(response.data?.deals || []);
      setTotalPages(response.data?.totalPages || 1);
    } catch (err) {
      console.error("Error fetching deals:", err);
      setError("Unable to connect to the deals feed right now.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Filter deals based on store selection and search query
  const filteredDeals = useMemo(() => {
    return deals.filter((deal) => {
      const text = (deal.content || "").toLowerCase();
      const matchesSearch = searchTerm
        ? text.includes(searchTerm.toLowerCase())
        : true;

      if (!matchesSearch) return false;

      if (selectedStore === "All") return true;
      if (selectedStore === "Amazon") return text.includes("amazon") || text.includes("amzn");
      if (selectedStore === "Flipkart") return text.includes("flipkart") || text.includes("fkrt");
      if (selectedStore === "Myntra") return text.includes("myntra");
      if (selectedStore === "Meesho") return text.includes("meesho");
      return true;
    });
  }, [deals, searchTerm, selectedStore]);

  return (
    <div className="container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="live-badge">
          <span className="live-dot" />
          Live Deals Feed
        </div>

        <h1 className="hero-title">
          Handpicked Deals & <span className="hero-title-highlight">Loot Offers</span>
        </h1>

        <p className="hero-subtitle">
          Real-time verified discounts, coupons, and flash deals across Amazon, Flipkart,
          Myntra & more. Join our channels to never miss any limited-time drop!
        </p>

        <div className="community-buttons">
          <a
            href="https://t.me/updealzz"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-community telegram"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.75-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .37z" />
            </svg>
            Join Telegram Channel
          </a>

          <a
            href="https://chat.whatsapp.com/EOSD1FRKL8DG9OXo5qfE2b?mode=ems_copy_t"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-community whatsapp"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2zm5.8 14.15c-.24.67-1.39 1.29-1.92 1.37-.5.08-1.13.11-3.28-.78-2.56-1.06-4.21-3.69-4.34-3.86-.13-.17-1.04-1.38-1.04-2.64 0-1.25.66-1.87.89-2.12.24-.25.52-.31.69-.31.18 0 .35 0 .5.01.16.01.38-.06.59.45.22.53.75 1.83.82 1.96.07.13.11.29.02.46-.08.18-.13.29-.26.44-.13.15-.28.34-.39.46-.13.13-.26.27-.11.53.15.25.66 1.09 1.42 1.77.98.87 1.8 1.14 2.06 1.27.26.13.41.11.56-.07.15-.17.65-.76.82-1.02.18-.26.35-.22.59-.13.24.09 1.54.73 1.8 1.02.27.29.44.43.51.55.07.12.07.7-.17 1.37z" />
            </svg>
            Join WhatsApp Group
          </a>
        </div>

        {/* Filter and Search Bar */}
        <div className="deals-filter-bar">
          <div className="search-input-wrapper">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search deals (e.g. shoes, watch, charger)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-tags">
            {STORES.map((store) => (
              <button
                key={store}
                className={`filter-tag ${selectedStore === store ? "active" : ""}`}
                onClick={() => setSelectedStore(store)}
              >
                {store}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Deals Feed */}
      {loading ? (
        <Loader />
      ) : error ? (
        <div className="state-box">
          <div className="state-icon">⚠️</div>
          <h2 className="state-title">Connection Error</h2>
          <p className="state-desc">{error}</p>
          <button className="btn-retry" onClick={() => fetchDeals(page)}>
            Retry Feed
          </button>
        </div>
      ) : filteredDeals.length === 0 ? (
        <div className="state-box">
          <div className="state-icon">🛍️</div>
          <h2 className="state-title">No Deals Found</h2>
          <p className="state-desc">
            {searchTerm || selectedStore !== "All"
              ? "No active deals match your current search or filter."
              : "No deals have been posted yet. New deals will appear live as they are published."}
          </p>
          {(searchTerm || selectedStore !== "All") && (
            <button
              className="btn-retry"
              onClick={() => {
                setSearchTerm("");
                setSelectedStore("All");
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <DealsList deals={filteredDeals} />
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default Home;
